'use client';

import { useEffect, useMemo } from 'react';
import { toApplicationGlobalHeaders } from '../electron/application-global-headers';
import { useApplicationById } from './api/useApplicationById';
import { useProxyStore } from '../store/proxy-store';
import { useTargetId, useTargetType } from '../store/projectStore';
import { TARGET_TYPE } from '../utils/target-scope.utils';

export function useSyncApplicationGlobalHeadersToProxy(): void {
  const targetId = useTargetId();
  const targetType = useTargetType();
  const isProxyRunning = useProxyStore((state) => state.isProxyRunning);
  const { application } = useApplicationById({
    id: targetId,
    enabled: targetType === TARGET_TYPE.WEB_APPLICATION,
  });

  const applicationGlobalHeaders = useMemo(() => {
    if (application?.id !== targetId) {
      return null;
    }

    return toApplicationGlobalHeaders(application.globalHeaders);
  }, [application, targetId]);

  useEffect(() => {
    if (!isProxyRunning || typeof window === 'undefined') {
      return;
    }

    window.electronAPI?.proxy
      .updateConfig({ applicationGlobalHeaders })
      .then((result) => {
        if (!result.success) {
          console.warn(
            'Failed to sync application global headers to proxy',
            new Error(result.error ?? 'Proxy rejected the config update'),
          );
        }
      })
      .catch((syncError: unknown) => {
        console.warn(
          'Failed to sync application global headers to proxy',
          syncError instanceof Error ? syncError : new Error(String(syncError)),
        );
      });
  }, [isProxyRunning, applicationGlobalHeaders]);
}
